<template>
  <div
    id="new-registrant"
    class="mt-3 container">
    <!-- Registrant, Price, & Registration Form -->
    <form
      id="new-registrant-form"
      name="newRegistrantForm"
      v-if="event && pricingLoaded && rosterID"
      @submit.prevent="submit(createRegistrantAndRegister, registrant)">
      <!-- Page Header -->
      <div class="row">
        <div class="col">
          <div class="d-flex flex-column flex-sm-row align-items-center">
            <h3 class="mb-0 mr-3 text-center text-sm-left">
              {{event.name}}: Add Registrant
            </h3>
          </div>
          <hr>
        </div>
      </div>
      <!-- / Page Header -->

      <!-- Base Data -->
      <RegistrantBaseData
        v-model="registrant"
      />
      <!-- / Base Data -->

      <!-- Pricing Package -->
      <div class="my-3">
        <h4 class="m-0 text-secondary">
          Select Pricing
          <hr>
        </h4>
      </div>
      <div
        class="row mt-3">
        <div class="col col-sm-6">
          <label for="price">Pricing: <small class="text-danger">required</small></label>
          <b-form-select
            id="price"
            ref="price"
            name="price"
            :disabled="pricing.length === 1"
            v-model="selectedPriceID">
            <option :value="undefined">
              {{pricing.length ? 'Select Price' : 'No pricing for this event.'}}
            </option>
            <option
              v-for="p in pricing"
              :key="p.id"
              :value="p.id">
              {{p.label}}
              ({{p.price_total_non_cc | currency}} + {{p.price_kjk_fees | currency}}
              credit card processing fee)
            </option>
          </b-form-select>
        </div>
      </div>
      <!-- / Pricing Package -->

      <!-- Registration Form -->
      <div class="my-3">
        <div class="d-flex align-items-center">
          <h4 class="m-0 text-secondary">
            Registration Form
            <small class="text-italic">(Optional)</small>
          </h4>
          <!-- Filter Fields -->
          <b-dropdown
            size="sm"
            variant="link"
            :text="`Show ${fieldFilter || 'All'} Fields`">
            <b-dropdown-item
              @click="fieldFilter = 'Waiver'">
              Show Waiver Fields
            </b-dropdown-item>
            <b-dropdown-item
              @click="fieldFilter = 'Non-Waiver'">
              Show Non-Waiver Fields
            </b-dropdown-item>
            <b-dropdown-item
              @click="fieldFilter = false">
              Show All Fields
            </b-dropdown-item>
          </b-dropdown>
          <!-- / Filter Fields -->
        </div>
        <hr>
      </div>

      <FormFields
        class="mt-3"
        :form-fields="orderedFields"
        :field-filter="fieldFilter"
        v-model="registration.form_data"
        hide-buttons
        hide-required
      />
      <!-- / Registration Form -->

      <div class="corner-buttons">
        <!-- Create Registration Button -->
        <b-button
          class="shadow mr-3"
          type="submit"
          variant="success"
          :disabled="submitting">
          <font-awesome-icon icon="user-plus"></font-awesome-icon> Register
        </b-button>
        <!-- / Create Registration Button -->

        <!-- Create Registration w/ Payment Dropdown -->
        <b-dropdown
          class="shadow"
          variant="success">
          <!-- Button -->
          <template slot="button-content">
            <font-awesome-icon icon="dollar-sign"></font-awesome-icon> Register With Payment
          </template>
          <!-- / Button -->

          <!-- Options -->
          <b-dropdown-item
            v-for="type in paymentTypeOptions"
            :key="type.type"
            @click="type.handler(type)">
            {{type.label}}
          </b-dropdown-item>
          <!-- / Options -->
        </b-dropdown>
        <!-- / Create Registration w/ Payment Dropdown -->

        <!-- Create Registration w/ Payment Button
        <b-button
          class="shadow ml-3"
          type="button"
          variant="success"
          :disabled="submitting"
          @click.prevent="showPayment">
          <font-awesome-icon icon="dollar-sign"></font-awesome-icon> Register With Payment
        </b-button>
        / Create Registration w/ Payment Button -->
      </div>
    </form>
    <!-- / Registrant, Price, & Registration Form -->

    <!-- Vue Stripe Checkout -->
    <vue-stripe-checkout
      ref="checkoutRef"
      image="https://stripe.com/img/documentation/checkout/marketplace.png"
      name="KJK Registrations"
      :description="`${event.org_name}: ${event.name}`"
      :amount="costInCents"
      :allow-remember-me="false"
      @done="checkoutDone">
    </vue-stripe-checkout>
    <!-- / Vue Stripe Checkout -->

    <!-- Payment Modal -->
    <b-modal
      id="payment-modal"
      ref="paymentModal"
      v-model="paymentModalOpen"
      :title="`Enter ${paymentType} Payment`"
      @hidden="payment = {}; paymentType = '';"
      hide-footer>
      <b-form
        id="payment-form"
        name="paymentForm"
        v-on:submit.prevent="submit(submitNonCCPayment, registrant, payment)">
        <!-- First Name -->
        <div class="row">
          <!-- First Name -->
          <div class="col">
            <label for="payment-first-name">Payor First Name:</label>
            <b-form-input
              id="payment-first-name"
              ref="paymenFirstName"
              name="paymenFirstName"
              type="text"
              :placeholder="`First Name${nonCash ? ` From ${paymentType}` : '' }`"
              v-model="payment.payment_first_name"
              :disabled="submitting">
            </b-form-input>
          </div>
          <!-- First Name -->
        </div>
        <!-- First Name -->

        <!-- Last Name -->
        <div class="row mt-3">
          <!-- Last Name -->
          <div class="col">
            <label for="payment-last-name">Payor Last Name:</label>
            <b-form-input
              id="payment-last-name"
              ref="paymenLastName"
              name="paymenLastName"
              type="text"
              :placeholder="`Last Name${nonCash ? ` From ${paymentType}` : '' }`"
              v-model="payment.payment_last_name"
              :disabled="submitting">
            </b-form-input>
          </div>
          <!-- Last Name -->
        </div>
        <!-- / Last Name -->

        <!-- Number -->
        <div
          v-if="nonCash"
          class="row mt-3">
          <div class="col">
            <label for="payment-number">{{paymentType}} Number:</label>
            <Number
              id="payment-number"
              ref="paymentNumber"
              name="paymentNumber"
              type="number"
              display-decimals="0"
              :placeholder="`Enter ${paymentType} Number`"
              step="1"
              v-model="payment.payment_number"
              :disabled="submitting"
            />
          </div>
        </div>
        <!-- / Number -->

        <!-- Amount -->
        <div class="row mt-3">
          <div class="col">
            <label for="payment-amount">Amount:</label>
            <Number
              id="payment-amount"
              ref="paymentAmount"
              name="paymentAmount"
              type="currency"
              :placeholder="`Enter ${paymentType} Amount`"
              step="10"
              v-model="payment.amount"
              :disabled="submitting"
            />
          </div>
        </div>
        <!-- / Amount -->

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button
            type="submit"
            class="ml-3"
            variant="success"
            :disabled="submitting">
            Register
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="paymentModalOpen = false;"
            :disabled="submitting">
            Cancel
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Payment Modal -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';
import RegistrantBaseData from '../components/RegistrantBaseData.vue';
import EventBus from '../services/event-bus';

// eslint-disable-next-line
let stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_API_KEY);

export default {
  name: 'new-registrant',
  components: {
    RegistrantBaseData,
  },
  mixins: [
    AppMixin,
    DirtyForm,
  ],
  data() {
    return {
      event: {},
      eventForm: {},
      registrant: {},
      registrantCopy: {},
      registration: {},
      registrationCopy: {},
      pricing: [],
      pricingLoaded: false,
      selectedPriceID: undefined,
      selectedPriceIDCopy: undefined,
      rosterID: null,
      fieldFilter: false,
      submitting: false,

      // payment
      payment: {},
      paymentModalOpen: false,
      paymentType: '',
      paymentTypeOptions: [
        {
          type: 'cash',
          label: 'Cash',
          handler: this.openNonCCPayment,
        },
        {
          type: 'check',
          label: 'Check',
          handler: this.openNonCCPayment,
        },
        {
          type: 'credit_card',
          label: 'Credit Card',
          handler: this.checkoutStart,
        },
        {
          type: 'money_order',
          label: 'Money Order',
          handler: this.openNonCCPayment,
        },
      ],
    };
  },
  computed: {
    costInCents() {
      const pricingPackage = _.find(this.pricing, { id: this.selectedPriceID }) || {};
      return parseFloat(pricingPackage.price_total) * 100;
    },
    orderedFields() {
      return _.orderBy(this.eventForm.form_fields, 'order');
    },
    registrantDirty() {
      const registrantJSON = JSON.stringify(this.registrant);
      const registrantCopyJSON = JSON.stringify(this.registrantCopy);
      return registrantJSON !== registrantCopyJSON;
    },
    registrationDirty() {
      const registrationJSON = JSON.stringify(this.registration);
      const registrationCopyJSON = JSON.stringify(this.registrationCopy);
      return registrationJSON !== registrationCopyJSON;
    },
    pricingDirty() {
      return this.selectedPriceID !== this.selectedPriceIDCopy;
    },
    anyDirty() {
      return this.registrantDirty || this.registrationDirty || this.pricingDirty;
    },
    nonCash() {
      return this.payment.payment_type !== 'cash';
    },
  },
  methods: {
    // general
    submit(action, ...args) {
      this.showToast('Registering...');
      this.submitting = true;

      return action(...args)
        .then(() => {
          this.$router.push({
            name: 'event',
            params: {
              eventID: this.event.id,
              tab: 'registrants',
            },
          });

          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Registration complete!');
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },

    // registrant & registration
    createRegistrantAndRegister(registrant, stripeSourceToken) {
      return DataSvc.createRegistrant(registrant)
        .then((r) => {
          const data = {
            registrant_id: r.id,
            roster_id: this.rosterID,
            complete_manual_registration: true,
            pricing_package_id: this.selectedPriceID,
            form_data: this.registration.form_data,
            stripe_source_token: stripeSourceToken,
          };

          this.registrant = r;
          this.registrantCopy = _.cloneDeep(r);

          return DataSvc.createRegistration(data);
        })
        .then((r) => {
          this.registration = r;
          this.registrationCopy = _.cloneDeep(this.registration);
          this.selectedPriceIDCopy = this.selectedPriceID;
          return r;
        });
    },

    // payment
    openNonCCPayment(type) {
      this.paymentType = type.label;
      this.payment = { payment_type: type.type };
      this.paymentModalOpen = true;
    },
    submitNonCCPayment(registrant, payment) {
      return this.createRegistrantAndRegister(registrant)
        .then(r => DataSvc.recordPayment(r.id, payment));
    },
    checkoutStart() {
      this.$refs.checkoutRef.open();
    },
    checkoutDone({ token }) {
      return this.submit(this.createRegistrantAndRegister, this.registrant, token.id);
    },
  },
  created() {
    this.showGlobalLoader('Loading data...');

    const initRegistrantNew = () => {
      const { eventID } = this.$route.params;

      // get event
      const eventPromise = DataSvc.getEvent(eventID)
        .then((event) => {
          this.event = event;

          if (this.loggedInUser.actingOrg.id !== event.org_id) {
            this.findAndSwitchActingOrg(event.org_id);
          }
        })
        .finally(() => {
          this.$nextTick(() => {
            // watch for org change and route to events page
            EventBus.$on('acting-org-changed', this.goToEvents);
          });
        });

      // get pricing for event
      const pricingPromise = DataSvc.getEventPricing(eventID)
        .then((pricing) => {
          this.pricing = pricing;
          this.pricingLoaded = true;

          if (pricing.length === 1) {
            this.selectedPriceID = pricing[0].id;
            this.selectedPriceIDCopy = pricing[0].id;
          }
        });

      // get first roster for event
      const rosterPromise = DataSvc.getRosters({ event_id: eventID })
        .then((rosters) => {
          this.rosterID = rosters[0].id;
        });

      // get event form fields
      const formPromise = DataSvc.getForms({ event_id: eventID })
        .then((eventForm) => {
          [this.eventForm] = eventForm;
        });

      return Promise.all([eventPromise, pricingPromise, rosterPromise, formPromise]);
    };

    this.initPromise.then(initRegistrantNew).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss"></style>
